import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import api from '../services/api';
import { hasPermission, getEffectivePermissions } from '../utils/permissions';
import { isSettingsLookupOnlyNav } from '../utils/settingsLookupNavAccess';
import SideNavLayout from './SideNavLayout';
import './SettingsLayout.css';

function getSettingsPath(id, tenantPath) {
  const rel = id === 'roles-permissions' ? '/settings/roles-permissions'
    : id === 'tenant-settings' ? '/settings/tenant-settings'
    : id === 'email-templates' ? '/settings/email-templates'
    : id === 'automation-rules' ? '/settings/automation-rules'
    : id === 'client-folder-template' ? '/settings/client-folder-template'
    : id === 'document-templates' ? '/settings/document-templates'
    : id === 'checklist-templates' ? '/settings/checklist-templates'
    : id === 'file-review-template' ? '/settings/file-review-template'
    : id === 'reset-user-password' ? '/settings/reset-user-password'
    : id === 'team' ? '/settings/team'
    : id === 'test-tools' ? '/settings/test-tools'
    : id === 'billable-seats' ? '/settings/billable-seats'
    : `/settings/${id}`;
  return tenantPath ? tenantPath(rel) : rel;
}

function SettingsLayout() {
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const pathname = location.pathname;

  const [showTestTools, setShowTestTools] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showFileReview, setShowFileReview] = useState(false);
  const [showClientWorkTasks, setShowClientWorkTasks] = useState(false);
  const [showAutomationRules, setShowAutomationRules] = useState(false);
  const [showAtPacketLookups, setShowAtPacketLookups] = useState(false);

  useEffect(() => {
    api.get('/whoami')
      .then((res) => {
        setShowTestTools(res.data?.is_super_admin === true || (res.data?.membership?.role_code && res.data.membership.role_code !== 'SERVICE_COORDINATOR'));
        setShowAutomationRules(res.data?.is_super_admin === true);
        setShowChecklist(res.data?.features?.checklist === true);
        setShowFileReview(res.data?.features?.file_review === true);
        setShowClientWorkTasks(res.data?.features?.client_work_tasks === true);
        setShowAtPacketLookups(res.data?.features?.at_packet === true);
      })
      .catch(() => {
        setShowTestTools(false);
        setShowAutomationRules(false);
        setShowChecklist(false);
        setShowFileReview(false);
        setShowClientWorkTasks(false);
        setShowAtPacketLookups(false);
      });
  }, []);

  const clientChildren = [
    { id: 'counties', name: 'Counties' },
    { id: 'cities', name: 'Cities' },
    { id: 'languages', name: 'Languages' },
    { id: 'races', name: 'Races' },
    { id: 'at-packet-dscc-statuses', name: 'DSCC statuses' },
  ];
  const referralChildren = [
    { id: 'living-arrangements', name: 'Living Arrangements' },
    { id: 'insurance-companies', name: 'Insurance Companies' },
    { id: 'insurance-types', name: 'Insurance Types' },
    { id: 'referral-level-1', name: 'Referral Source Level 1' },
    { id: 'referral-level-2', name: 'Referral Source Level 2' },
    { id: 'diagnosis-autoeligible', name: 'Diagnosis' },
    { id: 'termination-reasons', name: 'Termination Reasons' },
    { id: 'tracking-statuses', name: 'Tracking Statuses' },
  ];
  const providerChildren = [
    { id: 'provider-types', name: 'Provider Types' },
    { id: 'credential-codes', name: 'Credential Codes' },
    { id: 'service-areas', name: 'Service Areas' },
    { id: 'frequencies', name: 'Frequencies' },
    { id: 'delay-reasons', name: 'Delay Reasons' },
  ];
  const workTaskChildren = [
    { id: 'work-types', name: 'Work Types' },
    { id: 'task-types', name: 'Task Types' },
  ];
  const atPacketLookupChildren = useMemo(
    () => (showAtPacketLookups
      ? [
          { id: 'at-packet-statuses', name: 'Packet statuses' },
          { id: 'at-packet-insurance-types', name: 'Insurance types' },
          { id: 'at-packet-item-types', name: 'Item types' },
          { id: 'at-packet-item-names', name: 'Item names (AT)' },
        ]
      : []),
    [showAtPacketLookups]
  );
  const lookupOnlyNav = isSettingsLookupOnlyNav(getEffectivePermissions());

  const staffChildren = [
    { id: 'staff', name: 'Staff members' },
    { id: 'team', name: 'Team – Users & roles', permission: 'settings.assign_roles' },
    { id: 'reset-user-password', name: 'Force password reset', permission: 'settings.manage_staff' },
    { id: 'roles-permissions', name: 'Roles & permissions' },
  ].filter(item => !item.permission || hasPermission(item.permission));

  const canSeeBillableSeats =
    !lookupOnlyNav &&
    (hasPermission('settings.view') || hasPermission('settings.manage_lookups'));

  const topLevelItems = [
    { id: 'tenant-settings', name: 'Tenant Settings' },
    ...(canSeeBillableSeats ? [{ id: 'billable-seats', name: 'Billable seats' }] : []),
    { id: 'notification-templates', name: 'Scheduled job templates' },
    { id: 'email-templates', name: 'Email Templates', permission: 'email_templates.view' },
    ...(showAutomationRules ? [{ id: 'automation-rules', name: 'Automation rules (super admin)' }] : []),
    { id: 'document-templates', name: 'Document templates', permission: 'templates.manage' },
    { id: 'client-folder-template', name: 'Client Folder Template' },
    ...(showChecklist && hasPermission('checklists.manage_templates') ? [{ id: 'checklist-templates', name: 'Checklist Templates' }] : []),
    ...(showFileReview && hasPermission('file_reviews.manage_templates') ? [{ id: 'file-review-template', name: 'File Review Template' }] : []),
    ...(showTestTools ? [{ id: 'test-tools', name: 'Test tools' }] : []),
  ].filter(item => !item.permission || hasPermission(item.permission));

  const topLevelNavItems = lookupOnlyNav ? [] : topLevelItems;

  const isActive = (id) => {
    const base = pathname.replace(/\/$/, '');
    const target = getSettingsPath(id, tenantPath).replace(/\/$/, '');
    if (id === 'checklist-templates') {
      return base === target || base.startsWith(target + '/');
    }
    return base === target || base.startsWith(target + '/');
  };

  const isInGroup = (children) => children.some((c) => isActive(c.id));

  const [expandedGroups, setExpandedGroups] = useState(() => {
    const s = new Set();
    if (isInGroup(clientChildren)) s.add('client');
    if (isInGroup(referralChildren)) s.add('referral');
    if (isInGroup(providerChildren)) s.add('provider');
    if (isInGroup(workTaskChildren)) s.add('work-tasks');
    if (isInGroup(atPacketLookupChildren)) s.add('at-packet-lookups');
    if (isInGroup(staffChildren)) s.add('staff');
    return s;
  });

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (isInGroup(clientChildren)) next.add('client');
      if (isInGroup(referralChildren)) next.add('referral');
      if (isInGroup(providerChildren)) next.add('provider');
      if (isInGroup(workTaskChildren)) next.add('work-tasks');
      if (isInGroup(atPacketLookupChildren)) next.add('at-packet-lookups');
      if (isInGroup(staffChildren)) next.add('staff');
      return next;
    });
  }, [pathname, showAtPacketLookups]);

  const toggleGroup = (group) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const NavGroup = ({ groupKey, title, children }) => {
    const expanded = expandedGroups.has(groupKey);
    return (
      <div className="settings-layout-nav-group">
        <button
          type="button"
          className="settings-layout-nav-group-title"
          onClick={() => toggleGroup(groupKey)}
          aria-expanded={expanded}
        >
          <span>{title}</span>
          <span className="settings-layout-nav-chevron">{expanded ? '▼' : '▶'}</span>
        </button>
        {expanded && (
          <div className="settings-layout-nav-group-items">
            {children.map((child) => (
              <Link
                key={child.id}
                to={getSettingsPath(child.id, tenantPath)}
                className={`settings-layout-nav-item side-nav-link ${isActive(child.id) ? 'active' : ''}`}
              >
                {child.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  const nav = (
    <>
      <div className="settings-layout-nav-header side-nav-header">
        <h2 className="settings-layout-nav-title side-nav-title">Settings</h2>
      </div>
      <div className="settings-layout-nav-body">
        {!lookupOnlyNav && (
          <NavGroup groupKey="staff" title="Staff">
            {staffChildren}
          </NavGroup>
        )}
        <NavGroup groupKey="client" title="Client lookups">
          {clientChildren}
        </NavGroup>
        <NavGroup groupKey="referral" title="Referral lookups">
          {referralChildren}
        </NavGroup>
        <NavGroup groupKey="provider" title="Provider lookups">
          {providerChildren}
        </NavGroup>
        {!lookupOnlyNav && showClientWorkTasks && (
          <NavGroup groupKey="work-tasks" title="Work task lookups">
            {workTaskChildren}
          </NavGroup>
        )}
        {!lookupOnlyNav && atPacketLookupChildren.length > 0 && (
          <NavGroup groupKey="at-packet-lookups" title="AT packet lookups">
            {atPacketLookupChildren}
          </NavGroup>
        )}
        {topLevelNavItems.map((item) => (
          <Link
            key={item.id}
            to={getSettingsPath(item.id, tenantPath)}
            className={`settings-layout-nav-item side-nav-link settings-layout-nav-item-top ${isActive(item.id) ? 'active' : ''}`}
          >
            {item.name}
          </Link>
        ))}
      </div>
    </>
  );

  return (
    <div className="settings-layout">
      <SideNavLayout nav={nav} navClassName="settings-layout-nav" isolateCollapse />
    </div>
  );
}

export default SettingsLayout;
